// @ts-nocheck
/**
 * Count-up number animation for KPI tiles. When `value` changes to a new
 * numeric value, the displayed number tweens from the previous shown value
 * (or 0 on first mount) to the new value over `duration` ms using an
 * ease-out curve so the ramp feels snappy at the start and settles
 * gracefully at the target.
 *
 * Non-numeric values ('—', 'N/A', strings with letters) are rendered
 * verbatim without animation — a KPI that's still loading and shows '—'
 * won't try to interpolate to a NaN.
 *
 * The formatter is passed in so the caller controls thousand-separators
 * / decimals / suffixes.
 */
import { useEffect, useRef, useState } from 'react';

interface CountUpProps {
  value: number | string | null | undefined;
  duration?: number;
  formatter?: (n: number) => string;
}

const DEFAULT_FMT = (n: number) => Math.round(n).toLocaleString();

export function CountUp({ value, duration = 800, formatter = DEFAULT_FMT }: CountUpProps) {
  const target =
    value === null || value === undefined || value === ''
      ? null
      : typeof value === 'number'
      ? value
      : Number(value);

  const isNumericTarget = target !== null && Number.isFinite(target);
  const prevTargetRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);
  const [display, setDisplay] = useState<number>(isNumericTarget ? target : 0);

  useEffect(() => {
    if (!isNumericTarget) return;
    const from = prevTargetRef.current;
    const to = target as number;
    if (from === to) {
      setDisplay(to);
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic — fast start, gentle land
      const eased = 1 - Math.pow(1 - t, 3);
      const next = from + (to - from) * eased;
      setDisplay(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        prevTargetRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, isNumericTarget]);

  if (!isNumericTarget) {
    // Not a number — render whatever the caller passed as-is.
    return <>{value === null || value === undefined ? '—' : String(value)}</>;
  }

  return <>{formatter(display)}</>;
}
